import React, { useState, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, theme as antdTheme, Grid, Drawer, Button, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';
import {
  MenuOutlined,
  LogoutOutlined,
  UsergroupAddOutlined,
  MailOutlined,
  CodeOutlined,
  ArrowLeftOutlined,
  SettingOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  ApartmentOutlined,
  IdcardOutlined,
  TeamOutlined,
  AuditOutlined,
  DashboardOutlined,
  DollarOutlined,
  BankOutlined,
} from '@ant-design/icons';
const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
  isMobile?: boolean;
}

const SuperAdminNavigation = React.memo(() => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  return <SuperAdminSidebar isMobile={isMobile} />;
});

const SuperAdminSidebar = React.memo(({ isMobile }: SidebarProps) => {
  const { theme } = useTheme();
  const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
  const location = useLocation();
  const currentPath = location.pathname;
  const pathKeyMap: Record<string, string> = {
    '/super-admin/users': 'users',
    '/super-admin/staff-employees': 'staff-employees',
    '/super-admin/codevault-users': 'codevault-users',
    '/super-admin/codevault-admin': 'codevault-admin',
    '/super-admin/process-template-users': 'process-template-users',
    '/super-admin/asset-master-users': 'asset-master-users',
    '/super-admin/invoice-users': 'invoice-users',
    '/super-admin/report-users': 'report-users',
    '/super-admin/accounts-users': 'accounts-users',
    '/super-admin/hrms-users': 'hrms-users',
    '/super-admin/rooftop-admin': 'rooftop-admin',
    '/super-admin/login-logs': 'login-logs',
    '/super-admin/smtp-logs': 'smtp-logs',
    '/super-admin/purchase-order-users': 'purchase-order-users',
    '/super-admin/po-docs': 'po-docs',
    '/super-admin/hrms-departments': 'hrms-departments',
    '/super-admin/hrms-designations': 'hrms-designations',
    '/super-admin/reimbursement-users': 'reimbursement-users',
    '/super-admin/database-backups': 'database-backups',
    '/super-admin/telemetry': 'telemetry',
    '/super-admin/live-feed-config': 'live-feed-config',
    '/super-admin/sldc-data': 'sldc-data',
    '/super-admin/insurance-reminders': 'insurance-reminders',
    '/super-admin/project-finance-users': 'project-finance-users',
  };
  const currentKey = pathKeyMap[currentPath] ?? '';
          
  const { logout } = useAuth();
  const { isSuperAdmin } = usePermission();
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);

  const toggleDrawer = () => setDrawerVisible(v => !v);

  const handleBackToPrevious = useCallback(() => {
    navigate('/project-selection');
  }, [navigate]);

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    items.push({ 
      key: 'back-to-previous', 
      icon: <ArrowLeftOutlined />, 
      label: 'Back to Previous', 
      onClick: handleBackToPrevious,
      style: { marginBottom: '8px', borderBottom: '1px solid #f0f0f0' }
    });

    if (isSuperAdmin) {
      items.push({ key: 'users', icon: <UsergroupAddOutlined />, label: <Link to={`/super-admin/users`}>Platform Users</Link> });
      items.push({ key: 'staff-employees', icon: <UsergroupAddOutlined />, label: <Link to={`/super-admin/staff-employees`}>Staff Employees</Link> });
      items.push({ key: 'codevault-users', icon: <CodeOutlined />, label: <Link to={`/super-admin/codevault-users`}>CodeVault Users</Link> });
      items.push({ key: 'codevault-admin', icon: <SettingOutlined />, label: <Link to={`/super-admin/codevault-admin`}>CodeVault Admin</Link> });
      items.push({ key: 'process-template-users', icon: <FileTextOutlined />, label: <Link to={`/super-admin/process-template-users`}>Process Template Users</Link> });
      items.push({ key: 'asset-master-users', icon: <AppstoreOutlined />, label: <Link to={`/super-admin/asset-master-users`}>Asset Master Users</Link> });
      items.push({ key: 'invoice-users', icon: <FileTextOutlined />, label: <Link to={`/super-admin/invoice-users`}>Invoice Users</Link> });
      items.push({ key: 'report-users', icon: <AuditOutlined />, label: <Link to={`/super-admin/report-users`}>Report Users</Link> });
      items.push({ key: 'accounts-users', icon: <BankOutlined />, label: <Link to={`/super-admin/accounts-users`}>Accounts Users</Link> });
      items.push({ key: 'rooftop-admin', icon: <MailOutlined />, label: <Link to={`/super-admin/rooftop-admin`}>Rooftop Admin</Link> });
      items.push({ key: 'purchase-order-users', icon: <UsergroupAddOutlined />, label: <Link to={`/super-admin/purchase-order-users`}>Purchase Order Users</Link> });
      items.push({ key: 'po-docs', icon: <FileTextOutlined />, label: <Link to={`/super-admin/po-docs`}>PO Documents</Link> });
      items.push({ key: 'hrms-users', icon: <TeamOutlined />, label: <Link to={`/super-admin/hrms-users`}>HRMS Users</Link> });
      items.push({ key: 'hrms-departments', icon: <ApartmentOutlined />, label: <Link to={`/super-admin/hrms-departments`}>HRMS Departments</Link> });
      items.push({ key: 'hrms-designations', icon: <IdcardOutlined />, label: <Link to={`/super-admin/hrms-designations`}>HRMS Designations</Link> });
      items.push({ key: 'reimbursement-users', icon: <DollarOutlined />, label: <Link to={`/super-admin/reimbursement-users`}>Reimbursement Users</Link> });
      items.push({ key: 'database-backups', icon: <SettingOutlined />, label: <Link to={`/super-admin/database-backups`}>Database Backups</Link> });
      items.push({ key: 'telemetry', icon: <DashboardOutlined />, label: <Link to={`/super-admin/telemetry`}>Telemetry Dashboard</Link> });
      items.push({ key: 'live-feed-config', icon: <DashboardOutlined />, label: <Link to={`/super-admin/live-feed-config`}>Live Feed Config</Link> });
      items.push({ key: 'insurance-reminders', icon: <MailOutlined />, label: <Link to={`/super-admin/insurance-reminders`}>Insurance Reminders</Link> });
      items.push({ key: 'project-finance-users', icon: <DollarOutlined />, label: <Link to={`/super-admin/project-finance-users`}>Project Finance Users</Link> });
      items.push({ key: 'sldc-data', icon: <FileTextOutlined />, label: <Link to={`/super-admin/sldc-data`}>SLDC Data</Link> });
      items.push({ key: 'login-logs', icon: <MenuOutlined />, label: <Link to={`/super-admin/login-logs`}>Login Logs</Link> });
      items.push({ key: 'smtp-logs', icon: <MailOutlined />, label: <Link to={`/super-admin/smtp-logs`}>SMTP Logs</Link> });
    }
            
    items.push({ key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: logout, danger: true });
    
    return items;
  }, [logout, isSuperAdmin, handleBackToPrevious]);

  const menu = (
    <Menu
      selectedKeys={[currentKey]}
      theme={isDarkMode ? 'dark' : 'light'}
      mode="inline"
      items={menuItems}
      onClick={() => isMobile && setDrawerVisible(false)}
      style={{ border: 'none', paddingTop: '16px' }}
    />
  );

  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          icon={<MenuOutlined />}
          onClick={toggleDrawer}
          style={{ position: 'fixed', top: 16, left: 16, zIndex: 1000 }}
        />
        <Drawer
          placement="left"
          closable={false}
          onClose={toggleDrawer}
          open={drawerVisible}
          width="80%"
          bodyStyle={{ padding: 0, background: isDarkMode ? '#141414' : '#fff' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px' }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #1d8348 0%, #145a32 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Typography.Text style={{ color: '#ffffff', fontWeight: 700, fontSize: '16px', lineHeight: 1, letterSpacing: '-0.5px' }}>
                CC
              </Typography.Text>
            </div>
            <div>
              <Typography.Text style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: isDarkMode ? '#ffffff' : '#1a1a1a', lineHeight: 1.3 }}>
                Climate Connections
              </Typography.Text>
              <Typography.Text style={{ display: 'block', fontWeight: 500, fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)', lineHeight: 1.3, marginTop: '2px' }}>
                LLP
              </Typography.Text>
            </div>
          </div>
          {menu}
        </Drawer>
      </>
    );
  }

  return (
    <Sider
      width={220}
      theme={isDarkMode ? 'dark' : 'light'}
      style={{ 
        height: '100vh', 
        position: 'fixed', 
        left: 0, 
        boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        overflow: 'auto'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px' }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #1d8348 0%, #145a32 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Typography.Text style={{ color: '#ffffff', fontWeight: 700, fontSize: '16px', lineHeight: 1, letterSpacing: '-0.5px' }}>
            CC
          </Typography.Text>
        </div>
        <div>
          <Typography.Text style={{ display: 'block', fontWeight: 700, fontSize: '14px', color: isDarkMode ? '#ffffff' : '#1a1a1a', lineHeight: 1.3 }}>
            Climate Connections
          </Typography.Text>
          <Typography.Text style={{ display: 'block', fontWeight: 500, fontSize: '12px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)', lineHeight: 1.3, marginTop: '2px' }}>
            LLP
          </Typography.Text>
        </div>
      </div>
      {menu}
    </Sider>
  );
});

export default SuperAdminNavigation;
